/**
 * @fileoverview Singers Section component.
 * @summary Displays and manages the event singers.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/SingersSection
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faExclamationTriangle,
  faMicrophone,
  faTrash,
  faArrowRotateRight,
} from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { formatDate } from '../../../utils/date.js'
import Spinner from '../../../components/Spinner'
import type { Singer } from '../../../store/slices/singersSlice'

interface SingersSectionProps {
  isOpen: boolean
  isLoading: boolean
  error: string | null
  actionError: string | null
  singers: Singer[]
  onRefresh: () => void
  onDelete: (singerId: string) => void
}

/**
 * @summary Singers section component for displaying and managing event singers.
 * @param {SingersSectionProps} props - Component props.
 * @returns {React.ReactElement} Singers section or null if not open.
 */
export function SingersSection({
  isOpen,
  isLoading,
  error,
  actionError,
  singers,
  onRefresh,
  onDelete,
}: SingersSectionProps) {
  const { t, i18n } = useTranslation()

  if (!isOpen) {
    return null
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-(--color-text-primary) flex items-center gap-2">
          <FontAwesomeIcon icon={faUsers} />
          {t('events.singers')}
        </h2>
        <button
          onClick={onRefresh}
          className="btn btn-secondary btn-sm"
          disabled={isLoading}
          title={t('common.refresh')}
        >
          <FontAwesomeIcon 
            icon={faArrowRotateRight} 
            className={isLoading ? 'animate-spin' : ''}
          />
        </button>
      </div>

      {error && (
        <div className="alert alert-error mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
        </div>
      )}

      {actionError && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {actionError}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : singers.length === 0 ? (
        <p className="text-(--color-text-secondary)">{t('events.noSingers')}</p>
      ) : (
        <div className="space-y-2">
          {singers.map((singer) => (
            <div
              key={singer.id}
              className="p-3 bg-(--color-bg-secondary) rounded-lg hover:bg-(--color-bg-tertiary) transition-colors"
            >
              <div className="flex items-center gap-3">
                {/* Microphone icon */}
                <div className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-(--color-primary) text-white">
                  <FontAwesomeIcon icon={faMicrophone} className="w-5 h-5" />
                </div>

                {/* Singer info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-(--color-text-primary)">{singer.pseudo}</span>
                    {singer.guestMode && (
                      <span className="shrink-0 px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 rounded">
                        {t('events.guest')}
                      </span>
                    )}
                    <span className="shrink-0 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                      {t('events.songsCount', { count: singer.songsCount })}
                    </span>
                  </div>
                  <div className="text-sm text-(--color-text-secondary) mt-0.5">
                    {formatDate(singer.joinedAt, i18n.language)}
                  </div>
                </div>

                {/* Delete button */}
                <button
                  onClick={() => onDelete(singer.id)}
                  className="shrink-0 p-2 rounded hover:bg-red-100 dark:hover:bg-red-900/30"
                  title={t('events.deleteSinger')}
                >
                  <FontAwesomeIcon icon={faTrash} className="w-4 h-4 text-red-600 dark:text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

